/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import {
  X, FileSignature, Save, Send, Image as ImageIcon, Upload, FileText,
  Bold, Italic, Underline, Heading1, Heading2, AlignLeft, AlignCenter, AlignRight,
  AlignJustify, List, ListOrdered, Quote, Minus, BookmarkPlus, Calendar, Eye, Trash2,
  Sliders, Table as TableIcon, ExternalLink, Link as LinkIcon, RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/lib/auth.jsx";
import { handleEditorPaste } from "@/lib/editorPaste";
import PaginatedEditor from "@/components/PaginatedEditor";
import EditorToolbar from "@/components/EditorToolbar";

const FONTS = ["Inter", "Outfit", "Plus Jakarta Sans", "Roboto", "Playfair Display", "Lora", "Merriweather", "Montserrat", "Open Sans", "Times New Roman", "Georgia"];

/** Pull a Google Doc id out of a URL or accept a raw id. Mirrors the
 *  backend's `extract_gdoc_id` so the live preview iframe matches what
 *  the server will eventually clone. */
function extractGdocId(urlOrId) {
  if (!urlOrId) return "";
  const s = String(urlOrId).trim();
  if (!s.includes("/") && !s.includes("://")) return s; // already an id
  const m = s.match(/\/document\/d\/([a-zA-Z0-9_-]{20,})/);
  return m ? m[1] : "";
}

const MARGIN_PRESETS = {
  narrow: { top: 12, bottom: 12, left: 12, right: 12 },
  normal: { top: 25, bottom: 25, left: 25, right: 25 },
  wide:   { top: 40, bottom: 40, left: 50, right: 50 },
};

export default function LOABuilderModal({ mode = "issue", target, existing = null, onClose, onSuccess }) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [doc, setDoc] = useState(() => buildInitial(mode, target, existing, user));
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const editorRef = useRef(null);
  const docxRef = useRef(null);
  const wmFileRef = useRef(null);
  const logoFileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [r, b] = await Promise.all([
          api.get("/loa-templates", { params: { target_role: target.role } }),
          api.get("/admin/branding-defaults"),
        ]);
        setTemplates(r.data || []);
        // For brand-new "issue", pre-fill from branding defaults if user hasn't picked a template
        if (mode === "issue" && b.data) {
          setDoc((d) => ({
            ...d,
            logo_url: d.logo_url || b.data.logo_url,
            header_text: d.header_text || b.data.header_text || "",
            sub_header_text: d.sub_header_text || b.data.sub_header_text || "",
            footer_text: d.footer_text || b.data.footer_text || "",
            watermark_text: d.watermark_text || b.data.watermark_text || "",
            watermark_image_url: d.watermark_image_url || b.data.watermark_image_url,
            font_family: b.data.font_family || d.font_family,
            body_font_size: b.data.body_font_size || d.body_font_size,
            line_spacing: b.data.line_spacing || d.line_spacing,
            text_alignment: b.data.text_alignment || d.text_alignment,
            logo_size_pct: b.data.logo_size_pct || d.logo_size_pct,
          }));
        }
      } catch {}
    })();
    // Set editor content after mount
    setTimeout(() => {
      if (editorRef.current) editorRef.current.innerHTML = doc.content_html || "<p>Start writing…</p>";
    }, 60);
    // eslint-disable-next-line
  }, []);

  const applyTemplate = (id) => {
    setTplId(id);
    if (!id) return;
    const tpl = templates.find((t) => t.template_id === id);
    if (!tpl) return;
    setDoc((d) => ({
      ...d,
      content_html: tpl.content_html || "",
      source_gdoc_url: tpl.source_gdoc_url || "",
      source_gdoc_id: tpl.source_gdoc_id || "",
      header_text: tpl.header_text || "",
      sub_header_text: tpl.sub_header_text || "",
      footer_text: tpl.footer_text || "",
      watermark_text: tpl.watermark_text || "",
      watermark_image_url: tpl.watermark_image_url || null,
      logo_url: tpl.logo_url || null,
      logo_size_pct: tpl.logo_size_pct || 18,
      font_family: tpl.font_family || "Inter",
      body_font_size: tpl.body_font_size || 11,
      line_spacing: tpl.line_spacing || 1.5,
      text_alignment: tpl.text_alignment || "left",
    }));
    setTimeout(() => { if (editorRef.current) editorRef.current.innerHTML = tpl.content_html || ""; }, 50);
    toast.success(`Loaded: ${tpl.name}`);
  };

  const deleteTemplate = async () => {
    if (!tplId) return toast.error("Select a template to delete");
    const tpl = templates.find((t) => t.template_id === tplId);
    if (!tpl) return;
    if (tpl.is_global && user.role !== "admin") return toast.error("Only admin can delete global templates");
    if (!window.confirm(`Delete template "${tpl.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/loa-templates/${tplId}`);
      setTemplates((arr) => arr.filter((t) => t.template_id !== tplId));
      setTplId("");
      toast.success("Template deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const cmd = (c, v = null) => { document.execCommand(c, false, v); editorRef.current?.focus(); };

  const insertTable = () => {
    const rows = parseInt(window.prompt("Rows?", "3")) || 3;
    const cols = parseInt(window.prompt("Columns?", "3")) || 3;
    let html = '<table><thead><tr>';
    for (let c = 0; c < cols; c++) html += `<th>Header ${c + 1}</th>`;
    html += '</tr></thead><tbody>';
    for (let r = 0; r < rows; r++) {
      html += "<tr>";
      for (let c = 0; c < cols; c++) html += "<td>&nbsp;</td>";
      html += "</tr>";
    }
    html += "</tbody></table><p><br></p>";
    cmd("insertHTML", html);
  };

  const applyMarginPreset = (key) => {
    const m = MARGIN_PRESETS[key];
    if (!m) return;
    setDoc((d) => ({ ...d, margin_top: m.top, margin_bottom: m.bottom, margin_left: m.left, margin_right: m.right }));
  };

  const captureHtml = () => doc.content_html = editorRef.current?.innerHTML || doc.content_html;

  const uploadFile = (key) => (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setDoc((d) => ({ ...d, [key]: reader.result }));
    reader.readAsDataURL(file);
  };

  const importDocx = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/documents/import-docx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const html = r.data.content_html || "";
      setDoc((d) => ({ ...d, content_html: html }));
      setTimeout(() => { if (editorRef.current) editorRef.current.innerHTML = html; }, 50);
      toast.success("Word file imported");
    } catch (e) { toast.error("DOCX import failed: " + (e?.response?.data?.detail || e.message)); }
    if (docxRef.current) docxRef.current.value = "";
  };

  const saveAsTemplate = async () => {
    captureHtml();
    const name = window.prompt("Template name?", `LOA for ${ROLE_LABELS[target.role]}`);
    if (!name) return;
    try {
      await api.post("/loa-templates", {
        name,
        target_role: target.role,
        content_html: doc.content_html,
        source_gdoc_url: doc.source_gdoc_url || "",
        logo_url: doc.logo_url,
        logo_size_pct: doc.logo_size_pct,
        header_text: doc.header_text,
        sub_header_text: doc.sub_header_text,
        footer_text: doc.footer_text,
        watermark_text: doc.watermark_text,
        watermark_image_url: doc.watermark_image_url,
        font_family: doc.font_family,
        body_font_size: doc.body_font_size,
        line_spacing: doc.line_spacing,
        text_alignment: doc.text_alignment,
      });
      toast.success("Saved as template");
      const r = await api.get("/loa-templates", { params: { target_role: target.role } });
      setTemplates(r.data || []);
    } catch (e) { toast.error("Save template failed"); }
  };

  const openPreview = async () => {
    captureHtml();
    try {
      const previewCtx = {
        recipient_name: target.name,
        issuer_name: user.name,
        tenure_start: doc.tenure_start || "(start)",
        tenure_end: doc.tenure_end || "(end)",
        member_id: target.member_id || "—",
        role: ROLE_LABELS[target.role] || target.role,
      };
      const r = await api.post(
        "/documents/preview-pdf",
        { ...doc, preview_context: previewCtx },
        { responseType: "blob" }
      );
      // Revoke previous to free memory
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(r.data);
      setPreviewUrl(url);
    } catch (e) { toast.error("Preview failed"); }
  };

  const submit = async () => {
    captureHtml();
    if (!doc.tenure_end) return toast.error("End tenure date is required");
    const hasGdoc = !!(doc.source_gdoc_url || "").trim();
    if (!hasGdoc && (!doc.content_html || doc.content_html.trim() === "" || doc.content_html === "<br>")) {
      return toast.error("Paste a Google Doc link OR write the letter body");
    }
    setBusy(true);
    try {
      const payload = {
        ...doc,
        issued_to: target.user_id,
        template_id: tplId || null,
      };
      if (mode === "renew" && existing) {
        const r = await api.post(`/loas/${existing.loa_id}/renew`, payload);
        toast.success(`LOA renewed for ${target.name}` + (r.data.email_sent ? " · emailed" : " · SMTP not set"));
      } else {
        const r = await api.post("/loas/issue", payload);
        const sent = r.data.email_sent ? " · emailed" : " · SMTP not set";
        const cloned = r.data.gdoc_id ? " · Google Doc cloned & PDF attached" : "";
        toast.success(`LOA issued to ${target.name}${sent}${cloned}`);
      }
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-3xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col shadow-2xl" data-testid="loa-builder-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3 bg-gradient-to-r from-indigo-50 to-fuchsia-50">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white flex items-center justify-center">
            <FileSignature className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-black text-xl">{mode === "renew" ? "Renew LOA" : "Issue Letter of Appointment"}</h2>
            <p className="text-xs text-slate-500 truncate">To <b>{target.name}</b> · {target.email} · {ROLE_LABELS[target.role]}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-lg" data-testid="loa-close-btn"><X className="w-5 h-5" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 grid lg:grid-cols-5 gap-5 bg-slate-50/40">
          {/* Editor area */}
          <div className="lg:col-span-3 dashboard-card p-4">
            {/* Top controls: template + import */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <select className="input-pill text-xs py-2 flex-1 min-w-[200px]" value={tplId} onChange={(e) => applyTemplate(e.target.value)} data-testid="loa-template-select">
                <option value="">— Load saved template —</option>
                {templates.filter((t) => t.is_global).map((t) => (
                  <option key={t.template_id} value={t.template_id}>★ {t.name}</option>
                ))}
                {templates.filter((t) => !t.is_global).map((t) => (
                  <option key={t.template_id} value={t.template_id}>{t.name}{t.source_gdoc_url ? "  ·  ⎘ Google Doc" : ""}</option>
                ))}
              </select>
              {tplId && (
                <button onClick={deleteTemplate} className="btn-ghost text-xs text-rose-600" title="Delete selected template" data-testid="loa-delete-template">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              <button onClick={saveAsTemplate} className="btn-ghost text-xs" title="Save current as reusable template" data-testid="loa-save-template"><BookmarkPlus className="w-3.5 h-3.5" /> Save tpl</button>
            </div>

            {/* Title */}
            <input className="w-full text-xl font-display font-black outline-none border-b border-slate-100 pb-2 mb-3 placeholder:text-slate-300"
              value={doc.title}
              placeholder="Untitled letter"
              onChange={(e) => setDoc({ ...doc, title: e.target.value })}
              data-testid="loa-title-input"
            />

            {/* NEW: Google Doc link section — primary flow */}
            <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50/60 to-indigo-50/40 p-4 mb-3" data-testid="loa-gdoc-section">
              <div className="flex items-center gap-2 mb-2">
                <LinkIcon className="w-4 h-4 text-sky-700" />
                <h4 className="font-bold text-sm text-sky-900">Google Doc link</h4>
                <span className="text-[10px] text-sky-700/70">Paste the doc → we'll clone it per LOA, attach as PDF in the email</span>
              </div>
              <input
                type="url"
                placeholder="https://docs.google.com/document/d/…/edit"
                className="input-pill text-sm w-full"
                value={doc.source_gdoc_url || ""}
                onChange={(e) => setDoc({ ...doc, source_gdoc_url: e.target.value })}
                data-testid="loa-gdoc-url-input"
              />
              {extractGdocId(doc.source_gdoc_url) && (
                <div className="mt-3 rounded-xl overflow-hidden border border-sky-200 bg-white">
                  <div className="flex items-center justify-between px-3 py-2 bg-sky-50/70 border-b border-sky-100 text-xs text-sky-900">
                    <span className="flex items-center gap-1.5 truncate"><FileText className="w-3.5 h-3.5" /> Live preview of source doc</span>
                    <a href={doc.source_gdoc_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold hover:underline shrink-0" data-testid="loa-gdoc-open">
                      <ExternalLink className="w-3 h-3" /> Open in Google Docs
                    </a>
                  </div>
                  <iframe
                    src={`https://docs.google.com/document/d/${extractGdocId(doc.source_gdoc_url)}/preview`}
                    title="LOA source doc preview"
                    style={{ width: "100%", height: 460, border: "none" }}
                    data-testid="loa-gdoc-preview-iframe"
                  />
                  <div className="px-3 py-1.5 border-t border-sky-100 text-[11px] text-slate-500 bg-slate-50/70">
                    Preview not loading? The doc must be shared with the admin Google account, or set to "Anyone with link can view".
                  </div>
                </div>
              )}
              {!extractGdocId(doc.source_gdoc_url) && doc.source_gdoc_url && (
                <div className="mt-2 text-xs text-rose-600">That doesn't look like a Google Doc URL. Expected format: <code className="bg-rose-50 px-1 py-0.5 rounded">https://docs.google.com/document/d/…</code></div>
              )}
              {!doc.source_gdoc_url && (
                <div className="mt-2 text-[11px] text-slate-500">
                  Tip: type the recipient&apos;s name + tenure dates directly into the Google Doc before pasting the link — the doc is cloned on issue so the original template stays clean.
                </div>
              )}
            </div>

            {/* Fallback in-app editor — only shown when no Google Doc link is provided */}
            {!extractGdocId(doc.source_gdoc_url) && (
              <details className="mb-3" data-testid="loa-fallback-editor-wrap">
                <summary className="text-xs text-slate-500 cursor-pointer select-none hover:text-slate-700">No Google Doc? Click to write the letter inline (legacy mode)</summary>
                <div className="mt-3">
                  {/* Toolbar */}
                  <div className="flex flex-wrap gap-1 mb-2 p-1.5 rounded-xl bg-slate-50 border border-slate-100" data-testid="loa-toolbar">
                    <T onClick={() => cmd("bold")}><Bold className="w-3.5 h-3.5" /></T>
                    <T onClick={() => cmd("italic")}><Italic className="w-3.5 h-3.5" /></T>
                    <T onClick={() => cmd("underline")}><Underline className="w-3.5 h-3.5" /></T>
                    <Sep />
                    <T onClick={() => cmd("formatBlock", "h1")}><Heading1 className="w-3.5 h-3.5" /></T>
                    <T onClick={() => cmd("formatBlock", "h2")}><Heading2 className="w-3.5 h-3.5" /></T>
                    <T onClick={() => cmd("formatBlock", "blockquote")}><Quote className="w-3.5 h-3.5" /></T>
                    <T onClick={() => cmd("insertUnorderedList")}><List className="w-3.5 h-3.5" /></T>
                    <T onClick={() => cmd("insertOrderedList")}><ListOrdered className="w-3.5 h-3.5" /></T>
                    <Sep />
                    <T onClick={() => cmd("justifyLeft")}><AlignLeft className="w-3.5 h-3.5" /></T>
                    <T onClick={() => cmd("justifyCenter")}><AlignCenter className="w-3.5 h-3.5" /></T>
                    <T onClick={() => cmd("justifyRight")}><AlignRight className="w-3.5 h-3.5" /></T>
                    <T onClick={() => cmd("justifyFull")}><AlignJustify className="w-3.5 h-3.5" /></T>
                    <Sep />
                    <T onClick={insertTable} title="Insert table"><TableIcon className="w-3.5 h-3.5" /></T>
                    <T onClick={() => cmd("insertHTML", '<hr style="border:none;border-top:2px solid #e2e8f0;margin:1rem 0;" />')}><Minus className="w-3.5 h-3.5" /></T>
                    <Sep />
                    <label className="btn-ghost text-xs cursor-pointer py-1" data-testid="loa-import-docx">
                      <Upload className="w-3 h-3" /> Import .docx
                      <input ref={docxRef} type="file" accept=".docx,.doc" className="hidden" onChange={(e) => importDocx(e.target.files?.[0])} />
                    </label>
                    <button onClick={openPreview} className="btn-ghost text-xs py-1" data-testid="loa-preview-btn"><Eye className="w-3 h-3" /> Preview PDF</button>
                  </div>

                  <div className="text-[10px] text-slate-400 mb-2">Hint: use <code className="bg-slate-100 px-1 rounded">{`{{recipient_name}}`}</code>, <code className="bg-slate-100 px-1 rounded">{`{{tenure_start}}`}</code>, <code className="bg-slate-100 px-1 rounded">{`{{tenure_end}}`}</code> — auto-filled on issue.</div>

                  <EditorToolbar
                    editorRef={editorRef}
                    onChange={(html) => { doc.content_html = html; setDoc({ ...doc, content_html: html }); }}
                    className="mb-2"
                    testId="loa-editor-toolbar"
                  />

                  <div className="bg-white rounded-xl" data-testid="loa-page-mockup">
                    <PaginatedEditor
                      editorRef={editorRef}
                      initialHtml={doc.content_html}
                      onChange={(html) => { doc.content_html = html; }}
                      margins={{
                        top: doc.margin_top || 25,
                        bottom: doc.margin_bottom || 25,
                        left: doc.margin_left || 25,
                        right: doc.margin_right || 25,
                      }}
                      font={doc.font_family || "Inter"}
                      size={doc.body_font_size || 11}
                      lineHeight={doc.line_spacing || 1.5}
                      align={doc.text_alignment || "left"}
                      footerText={doc.footer_text || ""}
                      title={doc.title || ""}
                      testId="loa-paginated-editor"
                      page1Banner={(doc.logo_url || doc.header_text || doc.sub_header_text) ? (
                        <div className="border-b border-dashed border-slate-200 pb-3 mb-2 text-center" data-testid="loa-page1-banner">
                          {doc.logo_url && (
                            <img
                              src={doc.logo_url}
                              alt="logo"
                              className="mx-auto mb-1 object-contain"
                              style={{ height: `${0.6 + ((doc.logo_size_pct || 18) - 8) * (1.2 / 27)}cm`, maxHeight: "3.5cm" }}
                              data-testid="loa-page1-logo"
                            />
                          )}
                          {doc.header_text && <div className="font-bold text-slate-800" style={{ fontSize: "13pt", whiteSpace: "pre-line" }}>{doc.header_text}</div>}
                          {doc.sub_header_text && <div className="text-slate-500" style={{ fontSize: "10pt", whiteSpace: "pre-line" }}>{doc.sub_header_text}</div>}
                          <div className="text-[9px] uppercase tracking-wider text-slate-300 mt-1">— page 1 only —</div>
                        </div>
                      ) : null}
                    />
                  </div>
                </div>
              </details>
            )}
          </div>

          {/* Settings panel */}
          <div className="lg:col-span-2 space-y-3">
            {/* Tenure */}
            <div className="dashboard-card p-4 bg-gradient-to-br from-emerald-50 to-teal-50">
              <h4 className="font-bold text-sm mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-emerald-600" /> Tenure</h4>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-500">Start</label>
                  <input type="date" className="input-pill text-xs" value={doc.tenure_start || ""} onChange={(e) => setDoc({ ...doc, tenure_start: e.target.value })} data-testid="loa-tenure-start" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-500">End *</label>
                  <input type="date" className="input-pill text-xs" value={doc.tenure_end || ""} onChange={(e) => setDoc({ ...doc, tenure_end: e.target.value })} data-testid="loa-tenure-end" />
                </div>
              </div>
              {mode === "renew" && existing && (
                <div className="text-[10px] text-amber-700 mt-2">Currently expires: <b>{existing.tenure_end}</b></div>
              )}
            </div>

            {/* Branding */}
            <div className="dashboard-card p-4 space-y-3">
              <h4 className="font-bold text-sm flex items-center gap-2"><ImageIcon className="w-4 h-4 text-indigo-500" /> Branding</h4>

              {/* Logo */}
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Logo (page 1 only)</label>
                {doc.logo_url ? (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <img src={doc.logo_url} alt="logo" className="h-10 rounded border border-slate-200 object-contain" />
                      <button onClick={() => setDoc({ ...doc, logo_url: null })} className="text-xs text-rose-500">Remove</button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-500 w-12">Size</span>
                      <input type="range" min="8" max="32" value={doc.logo_size_pct || 18} onChange={(e) => setDoc({ ...doc, logo_size_pct: parseInt(e.target.value) })} className="flex-1" data-testid="loa-logo-size" />
                      <span className="text-[10px] text-slate-500 w-8">{doc.logo_size_pct || 18}%</span>
                    </div>
                  </div>
                ) : (
                  <label className="btn-ghost text-xs cursor-pointer w-full justify-center py-1.5">
                    <ImageIcon className="w-3 h-3" /> Upload logo
                    <input ref={logoFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile("logo_url")(e.target.files?.[0])} data-testid="loa-logo-upload" />
                  </label>
                )}
              </div>

              {/* Header / Sub-header / Footer */}
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Header text <span className="text-slate-400">(multi-line)</span></label>
                <textarea rows={2} className="input-pill text-xs" value={doc.header_text || ""} onChange={(e) => setDoc({ ...doc, header_text: e.target.value })} data-testid="loa-header" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Sub-header <span className="text-slate-400">(multi-line)</span></label>
                <textarea rows={2} className="input-pill text-xs" placeholder="Tagline&#10;Add a second line below" value={doc.sub_header_text || ""} onChange={(e) => setDoc({ ...doc, sub_header_text: e.target.value })} data-testid="loa-sub-header" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Footer text</label>
                <input className="input-pill text-xs" value={doc.footer_text || ""} onChange={(e) => setDoc({ ...doc, footer_text: e.target.value })} data-testid="loa-footer" />
              </div>

              {/* Watermark text + image */}
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Watermark text</label>
                <input className="input-pill text-xs" placeholder="OFFICIAL, CONFIDENTIAL…" value={doc.watermark_text || ""} onChange={(e) => setDoc({ ...doc, watermark_text: e.target.value })} data-testid="loa-watermark-text" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Watermark image (overrides text)</label>
                {doc.watermark_image_url ? (
                  <div className="flex items-center gap-2">
                    <img src={doc.watermark_image_url} alt="wm" className="h-10 rounded border border-slate-200 opacity-60" />
                    <button onClick={() => setDoc({ ...doc, watermark_image_url: null })} className="text-xs text-rose-500">Remove</button>
                  </div>
                ) : (
                  <label className="btn-ghost text-xs cursor-pointer w-full justify-center py-1.5" data-testid="loa-watermark-image-label">
                    <ImageIcon className="w-3 h-3" /> Upload watermark image
                    <input ref={wmFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile("watermark_image_url")(e.target.files?.[0])} data-testid="loa-watermark-image-upload" />
                  </label>
                )}
              </div>

              {/* Typography */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Font</label>
                  <select className="input-pill text-xs" value={doc.font_family} onChange={(e) => setDoc({ ...doc, font_family: e.target.value })}>
                    {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Size</label>
                  <select className="input-pill text-xs" value={doc.body_font_size} onChange={(e) => setDoc({ ...doc, body_font_size: parseInt(e.target.value) })}>
                    {[9, 10, 11, 12, 13, 14].map((s) => <option key={s} value={s}>{s}pt</option>)}
                  </select>
                </div>
              </div>

              {/* === Page margins === */}
              <div className="pt-2 border-t border-slate-100">
                <h5 className="font-bold text-xs flex items-center gap-1.5 mb-2"><Sliders className="w-3.5 h-3.5 text-indigo-500" /> Page margins</h5>
                <div className="grid grid-cols-3 gap-1 mb-2">
                  {Object.keys(MARGIN_PRESETS).map((k) => {
                    const active = (doc.margin_top === MARGIN_PRESETS[k].top) && (doc.margin_left === MARGIN_PRESETS[k].left);
                    return (
                      <button key={k} onClick={() => applyMarginPreset(k)}
                        className={`px-2 py-1 rounded-lg text-[10px] font-semibold capitalize border-2 transition ${active ? "border-indigo-400 bg-indigo-50 text-indigo-900" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                        data-testid={`loa-margin-${k}`}>
                        {k}
                      </button>
                    );
                  })}
                </div>
                {["top", "bottom", "left", "right"].map((side) => (
                  <div key={side} className="mb-1.5">
                    <div className="flex items-center justify-between text-[9px] uppercase font-bold text-slate-400">
                      <span>{side}</span>
                      <span className="text-slate-700">{doc[`margin_${side}`] ?? 25} mm</span>
                    </div>
                    <input type="range" min="5" max="60" step="1"
                      value={doc[`margin_${side}`] ?? 25}
                      onChange={(e) => setDoc({ ...doc, [`margin_${side}`]: parseInt(e.target.value) })}
                      className="w-full accent-indigo-500"
                      data-testid={`loa-margin-${side}-slider`}
                    />
                  </div>
                ))}
              </div>

              {/* Custom filename override */}
              <div className="pt-2 border-t border-slate-100">
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Download filename <span className="text-slate-400">(optional)</span></label>
                <input className="input-pill text-xs" placeholder={doc.title || "LOA"}
                  value={doc.filename || ""}
                  onChange={(e) => setDoc({ ...doc, filename: e.target.value })}
                  data-testid="loa-filename" />
                <div className="text-[9px] text-slate-400 mt-0.5">Default: uses the title above</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-slate-100 bg-white flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500 hidden md:block">Saving will email <b>{target.name}</b> a PDF + log to history</div>
          <div className="flex gap-2">
            <button onClick={openPreview} className="btn-ghost" data-testid="loa-preview-footer-btn"><Eye className="w-4 h-4" /> Preview</button>
            <button onClick={onClose} className="btn-ghost">Cancel</button>
            <button onClick={submit} disabled={busy} className="btn-primary" data-testid="loa-submit-btn">
              <Send className="w-4 h-4" /> {busy ? "Sending…" : (mode === "renew" ? "Renew & Email" : "Issue & Email")}
            </button>
          </div>
        </div>
      </div>

      {/* Preview overlay */}
      {previewUrl && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl h-[90vh] overflow-hidden flex flex-col" data-testid="loa-preview-modal">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="font-display font-bold flex items-center gap-2"><Eye className="w-5 h-5 text-indigo-500" /> A4 PDF preview</div>
              <div className="flex items-center gap-2">
                <a href={previewUrl} download="loa-preview.pdf" className="btn-ghost text-xs"><Send className="w-3.5 h-3.5" /> Download draft</a>
                <button onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="loa-preview-close"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <iframe src={previewUrl} title="LOA preview" className="flex-1 w-full bg-slate-200" />
          </div>
        </div>
      )}
    </div>
  );
}

function T({ onClick, children }) {
  return <button type="button" onClick={onClick} className="p-1.5 rounded hover:bg-white text-slate-700">{children}</button>;
}
function Sep() { return <div className="w-px bg-slate-200 mx-0.5" />; }

function buildInitial(mode, target, existing, user) {
  if (mode === "renew" && existing) {
    return {
      title: existing.title || `Letter of Appointment — ${target.name}`,
      content_html: existing.content_html || "",
      header_text: existing.header_text || "",
      sub_header_text: existing.sub_header_text || "",
      footer_text: existing.footer_text || "",
      watermark_text: existing.watermark_text || "",
      watermark_image_url: existing.watermark_image_url || null,
      logo_url: existing.logo_url || null,
      logo_size_pct: existing.logo_size_pct || 18,
      font_family: existing.font_family || "Inter",
      body_font_size: existing.body_font_size || 11,
      line_spacing: existing.line_spacing || 1.5,
      text_alignment: existing.text_alignment || "left",
      tenure_start: existing.tenure_start || new Date().toISOString().slice(0, 10),
      tenure_end: "", // force user to set new
    };
  }
  return {
    title: `Letter of Appointment — ${target.name}`,
    content_html: "",
    source_gdoc_url: "",
    source_gdoc_id: "",
    header_text: "",
    sub_header_text: "",
    footer_text: "",
    watermark_text: "",
    watermark_image_url: null,
    logo_url: null,
    logo_size_pct: 18,
    font_family: "Inter",
    body_font_size: 11,
    line_spacing: 1.5,
    text_alignment: "left",
    tenure_start: new Date().toISOString().slice(0, 10),
    tenure_end: "",
  };
}
